import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, map, Observable, of, switchMap } from 'rxjs';
import { environment } from 'environments/environment';
import { endpoints } from 'environments/endpoints';
import { User } from 'app/shared/models/actors-users/user.model';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Store } from '@ngrx/store';
import { UserStore } from 'app/core/user/user.types';
import { HttpHeadersUtil } from 'app/shared/utils/http-headers.util';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private baseRoute = environment.GATEWAY_API + endpoints.user.baseRoute;
  private subRoutes = endpoints.user.subRoutes;

  constructor(
    private _httpClient: HttpClient,
    private readonly _store: Store
  ) {}

  update(user: User): Observable<User> {
    return this._httpClient.put<User>(
      `${this.baseRoute}${this.subRoutes.update}`,
      user,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  updatePassword(email: string, password: string): Observable<any> {
    return this._httpClient.post<User>(
      `${this.baseRoute}${this.subRoutes.updatePassword + email + '/' + password}`,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  getAll(): Observable<User[]> {
    return this._httpClient.get<User[]>(
      `${this.baseRoute}${this.subRoutes.getAll}`,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  getById(id: string): Observable<User> {
    return this._httpClient.get<User>(
      `${this.baseRoute}${this.subRoutes.getById + id}`,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  getAllActiveByUserProfileCode(profileCode: string, id: string): Observable<User[]> {
    const currentUserId = id || '00000000-0000-0000-0000-000000000000';
    return this._httpClient.get<User[]>(
      `${this.baseRoute}${this.subRoutes.getAllActiveByUserProfileCode}${encodeURIComponent(profileCode)}/${currentUserId}`,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  getAllInterns(): Observable<User[]> {
    return this._httpClient.get<User[]>(
      `${this.baseRoute}${this.subRoutes.getAllInterns}`,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  getAllByActor(idActor: string): Observable<User[]> {
    return this._httpClient.get<User[]>(
      `${this.baseRoute}${this.subRoutes.getAllByActor + idActor}`,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  add(item: User): Observable<User> {
    return this._httpClient.post<User>(
      `${this.baseRoute}${this.subRoutes.add}`,
      item,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  edit(item: User): Observable<User> {
    return this._httpClient.put<User>(
      `${this.baseRoute}${this.subRoutes.update}`,
      item,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  getByEmail(email: string): Observable<any> {
    return this._httpClient
      .get(
        `${this.baseRoute}${this.subRoutes.getByEmail}` + email,
        HttpHeadersUtil.prepareDefaultHeaders()
      )
      .pipe(
        map(res => {
          return res as any;
        })
      );
  }

  getUserConnected(): Observable<User> {
    return this._store.pipe(
      map(store => store['user']?.connectedUser), // Récupère l'utilisateur connecté
      filter((connectedUser: UserStore | undefined) => !!connectedUser), // Vérifie s'il existe
      switchMap((connectedUser: UserStore) => this.getByEmail(connectedUser.email)) // Appelle getByEmail
    );
  }

  getDataChanges(id: string): Observable<string> {
    return this._httpClient.get<string>(
      `${this.baseRoute}${this.subRoutes.getDataChanges + id}`,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  getAllowedPermissionsByEmail(
    email: string,
    isTera?: boolean,
    isSica?: boolean
  ): Observable<FuseNavigationItem[]> {
    return this._httpClient.get<any>(
      `${this.baseRoute}${this.subRoutes.getAllowedPermissionsByUserEmail + email}${this.buildPlatformQuery(isTera, isSica)}`,
      HttpHeadersUtil.prepareDefaultHeaders()
    );
  }

  private buildPlatformQuery(isTera?: boolean, isSica?: boolean): string {
    const params = [];
    if (isTera !== undefined && isTera !== null) {
      params.push(`isTera=${isTera}`);
    }
    if (isSica !== undefined && isSica !== null) {
      params.push(`isSica=${isSica}`);
    }
    return params.length ? `?${params.join('&')}` : '';
  }
}
